import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Moon, Waves, Coffee } from "lucide-react";
import { loadBreathPulsePreference } from "@/lib/breathColorPulse";
import { loadBreathSyncPreference } from "@/lib/breathSync";
import { BreathColorPulse } from "@/components/calm/BreathColorPulse";

// A quiet space for when the mind is racing at bedtime.
//
// Two gentle modes, switchable via a soft tab bar:
//   • long breath — 4s in, 8s out. No counter, no cycles. Just an orb that
//                   breathes with you until you close it.
//   • body scan  — a slow drift of prompts from feet to crown, 25s each.
//
// The whole overlay dims further over ~10 minutes so if the user drifts off
// the screen doesn't glare. Nothing here counts, ranks, or scores.

const BODY_SCAN_STEPS = [
  "feet — let them soften.",
  "ankles — let them rest.",
  "calves — release the hold.",
  "knees — let them melt.",
  "thighs — feel their weight.",
  "hips — let them settle.",
  "lower back — release.",
  "belly — rise and fall.",
  "chest — soften around the heart.",
  "shoulders — let them fall.",
  "upper arms — heavy and warm.",
  "forearms and hands — let them rest.",
  "neck — release the effort.",
  "jaw — unclench, gently.",
  "tongue — soft on the roof of the mouth.",
  "eyes — heavy behind closed lids.",
  "forehead — smooth and quiet.",
  "the crown of your head — soft.",
  "your whole body — held.",
];

const STEP_MS = 25000; // 25s per body scan step
const AUTO_DIM_MS = 10 * 60 * 1000; // 10 minutes to full extra dim
const AUTO_DIM_MAX = 0.55; // opacity of the extra dim layer at full
const SESSION_MS = 30 * 60 * 1000; // 30 minutes before a gentle auto-close
const FADE_OUT_MS = 45000; // 45s soft fade from full to nothing
// After this long in Sleep Space, offer a discreet "still not feeling tired?"
// chip that opens a card explaining the CBT-I 20-minute rule. Long enough
// that we don't nag a user who is settling, short enough to catch a mind
// that's clearly not winding down.
const NOT_TIRED_HINT_MS = 5 * 60 * 1000;

// A soft, bell-like chime played once when a session gently closes. Two
// tones (E5 + a whispered G5) with a quick attack and a long exponential
// decay — no percussive edge. Falls back silently if AudioContext isn't
// available (older browsers or a suspended context).
const playSoftChime = () => {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const now = ctx.currentTime;
    const master = ctx.createGain();
    master.gain.value = 0.001;
    master.gain.exponentialRampToValueAtTime(0.14, now + 0.6);
    master.gain.exponentialRampToValueAtTime(0.0001, now + 6.5);
    master.connect(ctx.destination);

    [659.25, 783.99].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      g.gain.value = i === 0 ? 1 : 0.55;
      osc.connect(g).connect(master);
      osc.start(now);
      osc.stop(now + 7);
    });
    // Auto-close the context after the tail so we don't leak nodes.
    setTimeout(() => { ctx.close().catch(() => { /* noop */ }); }, 7500);
  } catch (_) {
    /* noop — chime is optional */
  }
};

export const SleepSpace = ({ open, onClose, onAudioAutoStart, onAudioFadeOut, onBreathPhase, onBreathEnd }) => {
  const [mode, setMode] = useState("breath"); // 'breath' | 'scan'
  const [scanIdx, setScanIdx] = useState(0);
  const [dim, setDim] = useState(0); // 0..AUTO_DIM_MAX
  const [wakeLockActive, setWakeLockActive] = useState(false);
  const [sessionEnding, setSessionEnding] = useState(false);
  // Track the current breath phase so the peripheral colour pulse can
  // cross-fade in sync. Only meaningful in `breath` mode; body-scan gets a
  // very slow neutral tint that changes with each step.
  const [breathPhase, setBreathPhase] = useState({ phase: "inhale", seconds: 4 });
  const [showNotTiredChip, setShowNotTiredChip] = useState(false);
  const [showTwentyMinRule, setShowTwentyMinRule] = useState(false);
  const pulseEnabled = loadBreathPulsePreference();
  const breathSyncEnabled = loadBreathSyncPreference();
  const wakeLockRef = useRef(null);

  const handleBreathPhaseChange = useCallback((phase, seconds) => {
    setBreathPhase({ phase, seconds });
    // Ride the ambient bed to match — swells on inhale, softens on exhale.
    // No-op if audio isn't playing or the user has turned sync off.
    if (breathSyncEnabled && typeof onBreathPhase === "function") {
      onBreathPhase(phase, seconds);
    }
  }, [breathSyncEnabled, onBreathPhase]);

  // Return the ambient bed to neutral whenever we leave breath mode or the
  // overlay closes, so audio never stays stuck at an inhale peak.
  useEffect(() => {
    const leavingBreath = !open || mode !== "breath" || sessionEnding;
    if (leavingBreath && typeof onBreathEnd === "function") {
      onBreathEnd();
    }
  }, [open, mode, sessionEnding, onBreathEnd]);

  // Reset state and softly auto-start the drone audio each time the overlay
  // opens (respects the userEngagedAudio flag inside AudioControls so it
  // never overrides a manual choice).
  useEffect(() => {
    if (!open) return;
    setMode("breath");
    setScanIdx(0);
    setDim(0);
    setSessionEnding(false);
    setShowNotTiredChip(false);
    setShowTwentyMinRule(false);
    if (typeof onAudioAutoStart === "function") onAudioAutoStart("drone");
  }, [open, onAudioAutoStart]);

  // After 5 minutes in Sleep Space, offer a soft "still not feeling tired?"
  // chip. Tapping it opens the CBT-I 20-minute-rule card. Never fires during
  // the auto-close fade and clears cleanly on unmount.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      setShowNotTiredChip(true);
    }, NOT_TIRED_HINT_MS);
    return () => clearTimeout(t);
  }, [open]);

  // Gentle session close — after SESSION_MS the overlay softly fades over
  // FADE_OUT_MS and plays a bell-like chime, so a session never loops
  // forever if the user drifts off. The fade is driven by framer-motion's
  // animate prop (see the motion.div below) and onClose is called after the
  // fade completes. The ambient audio ducks down over the same window so
  // the session ends whole instead of cutting off.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      setSessionEnding(true);
      playSoftChime();
      if (typeof onAudioFadeOut === "function") {
        onAudioFadeOut(FADE_OUT_MS / 1000);
      }
    }, SESSION_MS);
    return () => clearTimeout(t);
  }, [open, onAudioFadeOut]);

  // Screen Wake Lock — keeps the phone from dimming/locking mid-session so a
  // 10-minute body scan or long breathing session isn't interrupted. Paired
  // with the auto-dim layer above so users can still drift off gracefully:
  // the screen stays *on* but the overlay slowly darkens.
  //
  // Browsers automatically release the wake lock when the tab is hidden, so
  // we re-request on visibilitychange while the overlay is open.
  useEffect(() => {
    if (!open) return;
    if (typeof navigator === "undefined" || !("wakeLock" in navigator)) return;

    let cancelled = false;

    const request = async () => {
      try {
        const lock = await navigator.wakeLock.request("screen");
        if (cancelled) {
          try { await lock.release(); } catch (_) { /* noop */ }
          return;
        }
        wakeLockRef.current = lock;
        setWakeLockActive(true);
        lock.addEventListener("release", () => {
          setWakeLockActive(false);
        });
      } catch (_) {
        // Permissions/policy or a page-not-visible edge — silently keep
        // the session going without the lock.
        setWakeLockActive(false);
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible" && !wakeLockRef.current) {
        request();
      }
    };

    request();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      const lock = wakeLockRef.current;
      wakeLockRef.current = null;
      setWakeLockActive(false);
      if (lock) {
        lock.release().catch(() => { /* noop */ });
      }
    };
  }, [open]);

  // Body-scan drift: advance one step every STEP_MS. Loops back to the top
  // so if someone leaves it running past the end it just keeps flowing.
  useEffect(() => {
    if (!open || mode !== "scan") return;
    const t = setInterval(
      () => setScanIdx((i) => (i + 1) % BODY_SCAN_STEPS.length),
      STEP_MS,
    );
    return () => clearInterval(t);
  }, [open, mode]);

  // Auto-dim: linearly ramp a black overlay's opacity from 0 to AUTO_DIM_MAX
  // over AUTO_DIM_MS. Uses a real interval (not CSS transition) so we can
  // sample the elapsed time correctly if the browser throttles background.
  useEffect(() => {
    if (!open) return;
    const started = performance.now();
    const t = setInterval(() => {
      const elapsed = performance.now() - started;
      const p = Math.min(elapsed / AUTO_DIM_MS, 1);
      setDim(p * AUTO_DIM_MAX);
    }, 3000);
    return () => clearInterval(t);
  }, [open]);

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="sleep-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: sessionEnding ? 0 : 1 }}
        exit={{ opacity: 0 }}
        transition={{
          duration: sessionEnding ? FADE_OUT_MS / 1000 : 1.6,
          ease: "easeInOut",
        }}
        onAnimationComplete={() => {
          if (sessionEnding && typeof onClose === "function") onClose();
        }}
        className="fixed inset-0 z-30 overflow-y-auto overscroll-contain"
        data-testid="sleep-overlay"
        data-session-ending={sessionEnding}
      >
        {/* Baseline blur veil */}
        <div className="fixed inset-0 bg-black/50 backdrop-blur-2xl pointer-events-none" aria-hidden="true" />

        {/* Peripheral colour-therapy pulse — only during breath mode, where
            we have a real phase timeline to sync to. */}
        {mode === "breath" && (
          <BreathColorPulse
            active={pulseEnabled && !sessionEnding}
            phase={breathPhase.phase}
            seconds={breathPhase.seconds}
          />
        )}
        {/* Slow-growing extra dim layer for drifting-off */}
        <div
          className="fixed inset-0 pointer-events-none"
          style={{ backgroundColor: `rgba(0,0,0,${dim})` }}
          aria-hidden="true"
          data-testid="sleep-dim-layer"
        />

        {/* Scrollable centering container — flex-centered when short, scrolls
            when content doesn't fit (landscape phones, tiny screens). */}
        <div className="relative min-h-full flex items-center justify-center px-6 py-24 sm:py-20">

        <button
          onClick={onClose}
          className="fixed w-10 h-10 rounded-full flex items-center justify-center bg-white/5 border border-white/10 backdrop-blur-xl text-white/60 hover:bg-white/10 hover:text-white transition-colors duration-500 z-10"
          style={{
            top: "calc(env(safe-area-inset-top) + 1rem)",
            right: "calc(env(safe-area-inset-right) + 1rem)",
          }}
          data-testid="sleep-close-btn"
          aria-label="Close sleep space"
        >
          <X size={18} strokeWidth={1.5} />
        </button>

        {/* Subtle wake-lock indicator — a small dot + label in the bottom-left
            corner. Only shown when the browser confirms the lock is active,
            so users know the screen won't dim itself out. */}
        <AnimatePresence>
          {wakeLockActive && (
            <motion.div
              key="wake-indicator"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.4, ease: "easeInOut" }}
              className="fixed flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-white/30 pointer-events-none z-10"
              style={{
                bottom: "calc(env(safe-area-inset-bottom) + 1rem)",
                left: "calc(env(safe-area-inset-left) + 1rem)",
              }}
              data-testid="sleep-wake-indicator"
              aria-label="Screen awake"
            >
              <motion.span
                className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-300/70"
                animate={{ opacity: [0.35, 0.9, 0.35] }}
                transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
              />
              <span>screen awake</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Mode tabs */}
        <div
          className="fixed left-1/2 -translate-x-1/2 flex items-center gap-1 rounded-full backdrop-blur-xl bg-white/5 border border-white/10 px-1 py-1 z-10"
          style={{ top: "calc(env(safe-area-inset-top) + 1rem)" }}
          data-testid="sleep-mode-tabs"
          role="tablist"
        >
          {[
            { id: "breath", label: "long breath", Icon: Waves },
            { id: "scan", label: "body scan", Icon: Moon },
          ].map((m) => {
            const active = mode === m.id;
            return (
              <button
                key={m.id}
                role="tab"
                aria-selected={active}
                onClick={() => setMode(m.id)}
                className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs transition-colors duration-500 ${
                  active
                    ? "bg-white/15 text-white/95"
                    : "text-white/45 hover:text-white/75"
                }`}
                data-testid={`sleep-tab-${m.id}`}
                data-active={active}
              >
                <m.Icon size={13} strokeWidth={1.5} />
                <span>{m.label}</span>
              </button>
            );
          })}
        </div>

        <div className="relative w-full max-w-lg flex flex-col items-center text-center">
          {mode === "breath" ? (
            <BreathMode onPhaseChange={handleBreathPhaseChange} />
          ) : (
            <ScanMode step={BODY_SCAN_STEPS[scanIdx]} idx={scanIdx} />
          )}

          {/* Soft nudge that appears after 5 minutes — for the mind that just
              isn't winding down. Tapping opens the CBT-I 20-minute-rule card. */}
          <AnimatePresence>
            {showNotTiredChip && !showTwentyMinRule && !sessionEnding && (
              <motion.button
                key="not-tired-chip"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 1.4, ease: "easeInOut" }}
                onClick={() => setShowTwentyMinRule(true)}
                className="mt-14 inline-flex items-center gap-2 rounded-full backdrop-blur-xl bg-white/8 border border-white/15 px-5 py-2.5 text-white/75 hover:bg-white/12 hover:text-white/95 text-sm transition-colors duration-500"
                data-testid="sleep-not-tired-chip"
                aria-label="Open the 20-minute rule for restless nights"
              >
                <Coffee size={14} strokeWidth={1.5} />
                <span>still not feeling tired?</span>
              </motion.button>
            )}
          </AnimatePresence>

          <p className="mt-16 text-[10px] tracking-[0.3em] uppercase text-white/25">
            close whenever you are ready
          </p>
        </div>

        {/* CBT-I 20-minute-rule card — an educational overlay layered above
            the sleep space. Dismissing returns the user to the same session. */}
        <AnimatePresence>
          {showTwentyMinRule && (
            <motion.div
              key="sleep-twenty-min-rule"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.9, ease: "easeInOut" }}
              className="fixed inset-0 z-20 overflow-y-auto overscroll-contain flex items-center justify-center px-6 py-16"
              data-testid="sleep-twenty-min-rule"
            >
              <div className="fixed inset-0 bg-black/55 backdrop-blur-xl pointer-events-none" aria-hidden="true" />
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                transition={{ duration: 1.0, ease: "easeInOut" }}
                className="relative w-full max-w-md rounded-3xl backdrop-blur-xl bg-white/[0.07] border border-white/15 px-6 py-8 sm:px-8 sm:py-10 text-left"
                onClick={(e) => e.stopPropagation()}
              >
                <h2 className="font-heading italic text-white/95 text-2xl sm:text-3xl leading-snug">
                  the 20-minute rule for sleep conditioning
                </h2>
                <p className="mt-5 text-white/70 text-sm sm:text-[15px] leading-relaxed">
                  Remaining in bed while anxious or alert creates a
                  psychological link between your sleep environment and
                  wakefulness. To preserve your bed as a cue for rest, follow
                  the 20-minute principle:
                </p>

                <ul className="mt-5 space-y-4 pl-5 list-disc marker:text-white/30 text-white/70 text-sm sm:text-[15px] leading-relaxed">
                  <li>
                    <span className="text-white/90 font-medium">
                      Leave the Bedroom:
                    </span>{" "}
                    If you remain awake after approximately 20 minutes,
                    transition to another room.
                  </li>
                  <li>
                    <span className="text-white/90 font-medium">
                      Engage in Low-Stimulation Activities:
                    </span>{" "}
                    Keep lighting low and avoid electronic displays. Opt for
                    relaxing tasks such as soft stretching or reading printed
                    media.
                  </li>
                  <li>
                    <span className="text-white/90 font-medium">
                      Wait for Drowsiness:
                    </span>{" "}
                    Return to bed only when physically sleepy to reinforce
                    positive sleep associations.
                  </li>
                </ul>

                <div className="mt-7 flex justify-end">
                  <button
                    onClick={() => setShowTwentyMinRule(false)}
                    className="inline-flex items-center gap-2 rounded-full px-5 py-2 bg-white/10 hover:bg-white/15 border border-white/15 text-white/85 hover:text-white text-sm transition-colors duration-500"
                    data-testid="sleep-twenty-min-close"
                  >
                    got it
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

// A silent breathing orb — 4s inhale, 8s exhale, on loop. No numbers, no
// counter, no cycle count. Just a body cue you can watch or ignore.
// Emits `onPhaseChange(phase, seconds)` so callers (colour pulse) can sync.
const INHALE_MS = 4000;
const EXHALE_MS = 8000;
const BreathMode = ({ onPhaseChange }) => {
  useEffect(() => {
    if (typeof onPhaseChange !== "function") return;
    // Kick off in the inhale phase immediately so the pulse joins right away.
    onPhaseChange("inhale", INHALE_MS / 1000);
    let inhaleTimer;
    let exhaleTimer;
    const tick = () => {
      // We're now inhaling. Schedule the exhale to fire at the top of the exhale
      // half of the 12s cycle. Chain a fresh cycle after the exhale ends.
      inhaleTimer = setTimeout(() => {
        onPhaseChange("exhale", EXHALE_MS / 1000);
        exhaleTimer = setTimeout(() => {
          onPhaseChange("inhale", INHALE_MS / 1000);
          tick();
        }, EXHALE_MS);
      }, INHALE_MS);
    };
    tick();
    return () => {
      clearTimeout(inhaleTimer);
      clearTimeout(exhaleTimer);
    };
  }, [onPhaseChange]);

  return (
    <div className="flex flex-col items-center gap-10">
      <div
        className="relative flex items-center justify-center h-80 w-80"
        data-testid="sleep-breath-orb"
      >
        <motion.div
          className="absolute rounded-full"
          animate={{ scale: [0.85, 1.55, 1.55, 0.85], opacity: [0.5, 0.85, 0.85, 0.5] }}
          transition={{
            times: [0, 0.33, 0.34, 1], // 4s inhale, then 8s exhale — 12s cycle
            duration: 12,
            ease: "easeInOut",
            repeat: Infinity,
          }}
          style={{
            width: "13rem",
            height: "13rem",
            background:
              "radial-gradient(circle, rgba(167,139,250,0.55) 0%, rgba(34,211,238,0.2) 55%, transparent 80%)",
            filter: "blur(28px)",
          }}
        />
        <motion.div
          className="w-44 h-44 rounded-full border border-white/15 backdrop-blur-md bg-white/[0.03]"
          animate={{ scale: [0.85, 1.55, 1.55, 0.85] }}
          transition={{
            times: [0, 0.33, 0.34, 1],
            duration: 12,
            ease: "easeInOut",
            repeat: Infinity,
          }}
        />
      </div>
      <p className="font-heading italic text-white/70 text-xl">
        breathe in slowly, breathe out longer.
      </p>
    </div>
  );
};

const ScanMode = ({ step, idx }) => {
  return (
    <div
      className="flex flex-col items-center gap-8"
      data-testid="sleep-scan"
      data-scan-idx={idx}
    >
      <AnimatePresence mode="wait">
        <motion.p
          key={idx}
          initial={{ opacity: 0, y: 12, filter: "blur(6px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          exit={{ opacity: 0, y: -8, filter: "blur(6px)" }}
          transition={{ duration: 2.4, ease: "easeInOut" }}
          className="font-heading italic text-white/85 text-3xl sm:text-4xl leading-relaxed max-w-md"
          data-testid="sleep-scan-line"
        >
          {step}
        </motion.p>
      </AnimatePresence>
      <p className="text-white/40 text-sm max-w-sm leading-relaxed">
        keep your eyes closed if you like. this will drift on its own.
      </p>
    </div>
  );
};

export default SleepSpace;
